/**
 * Builds the HUD snapshot from a match state. Pure and cheap (about a hundred field reads), run
 * at 20 Hz by the game loop.
 */
import {
  otherSlot,
  secondsToTicks,
  type Combatant,
  type MatchEvent,
  type MatchState,
  type Slot,
} from '@steelwake/shared/combat';
import {
  BALANCE,
  getAbility,
  getMech,
  getWeapon,
  type MechDefinition,
} from '@steelwake/shared/data';
import { SIM_TICK_RATE } from '@steelwake/shared';
import type { PaletteName } from '@steelwake/shared/progression';
import { colors, hudPalette } from '@steelwake/ui';

import type { HudEvent, HudMech, HudSnapshot, HudStats, HudWeapon, OnlineHud } from './types.js';

function statsOf(s: MatchState['stats'][number] | undefined): HudStats {
  return {
    damageDealt: s?.damageDealt ?? 0,
    kills: s?.kills ?? 0,
    shotsFired: s?.shotsFired ?? 0,
    shotsHit: s?.shotsHit ?? 0,
    roundsWon: s?.roundsWon ?? 0,
  };
}

function mechPanel(
  c: Combatant,
  def: MechDefinition,
  name: string,
  concealed = false,
  accent: string | null = null,
): HudMech {
  // A concealment zone (Phase 8) hides a mech the way a cloak does.
  const cloaked = concealed || c.effects.some((e) => e.params.flags?.includes('cloaked'));
  return {
    name,
    mechId: def.id,
    // A colour-blind palette paints the sides in fixed colours instead of the mechs' accents.
    accent: accent ?? def.accent,
    hp: c.hp,
    maxHp: def.hp,
    shield: c.shield,
    maxShield: def.shield,
    energy: c.body.energy,
    maxEnergy: def.energy,
    heat: c.heat,
    heatCap: def.heatCap,
    overheated: c.overheatTicks > 0,
    venting: c.venting,
    alive: c.alive,
    legDamage: Math.min(1, c.legDamage / (def.hp * BALANCE.legDamage.atHpFraction)),
    armDamage: Math.min(1, c.armDamage / (def.hp * BALANCE.armDamage.atHpFraction)),
    effects: c.effects.filter((e) => e.kind !== 'knockback').map((e) => e.kind),
    cloaked,
  };
}

function weapons(c: Combatant, def: MechDefinition): HudWeapon[] {
  return c.weapons.map((w, i) => {
    const wd = getWeapon(w.id);
    const coolTicks = wd.beam ? secondsToTicks(wd.beam.coolSeconds) : 0;
    return {
      name: wd.name,
      slot: def.weapons[i]?.slot ?? 'primary',
      kind: wd.kind,
      ammo: w.ammo,
      magazine: wd.magazine?.size ?? null,
      reloading: w.reloadTicks > 0,
      cooling: coolTicks > 0 ? w.beamCoolTicks / coolTicks : 0,
    };
  });
}

export function buildHudSnapshot(
  state: MatchState,
  mySlot: Slot,
  aimYaw: number,
  online: OnlineHud | null = null,
  palette: PaletteName = 'default',
  concealed: readonly [boolean, boolean] = [false, false],
): HudSnapshot {
  const p = hudPalette(palette);
  const enemySlot = otherSlot(mySlot);
  const me = state.mechs[mySlot];
  const enemy = state.mechs[enemySlot];
  const myDef = getMech(me.mechId);
  const enemyDef = getMech(enemy.mechId);
  const abilityDef = getAbility(myDef.abilities[0]!.id);
  const ultDef = getAbility(myDef.ultimate);
  const dx = enemy.body.position.x - me.body.position.x;
  const dz = enemy.body.position.z - me.body.position.z;
  // Bearing: angle from the aim direction to the enemy, clockwise positive.
  const toEnemyYaw = Math.atan2(-dx, -dz);
  let bearing = toEnemyYaw - aimYaw;
  bearing = Math.atan2(Math.sin(bearing), Math.cos(bearing));
  const enemyCloaked = enemy.effects.some((e) => e.params.flags?.includes('cloaked'));
  const distance = Math.hypot(dx, dz);
  return {
    phase: state.flow.phase,
    round: state.flow.round,
    wins: [state.flow.wins[0], state.flow.wins[1]],
    roundsToWin: state.config.roundsToWin,
    timeLeft: state.flow.ticksLeft / SIM_TICK_RATE,
    suddenDeath: state.flow.phase === 'suddenDeath',
    // Online the pilots' callsigns name the panels; practice shows the mech names.
    me: mechPanel(me, myDef, online?.callsigns[mySlot] ?? myDef.name, concealed[mySlot], p.me),
    enemy: mechPanel(
      enemy,
      enemyDef,
      online?.callsigns[enemySlot] ?? enemyDef.name,
      concealed[enemySlot],
      p.enemy,
    ),
    weapons: weapons(me, myDef),
    ability: {
      name: abilityDef.name,
      cooldown: me.abilities[0].cooldown,
      cooldownMax: myDef.abilities[0]!.cooldown,
      active: me.abilities[0].active,
      toggled: me.abilities[0].toggled,
    },
    ultimate: { name: ultDef.name, charge: me.ultCharge },
    radar: { bearing: -bearing, distance, visible: !enemyCloaked || distance <= 6 },
    roundWinner: state.flow.roundWinner,
    lastRound: state.rounds.at(-1) ?? null,
    result: state.result,
    myScore: state.result?.scores[mySlot] ?? null,
    enemyScore: state.result?.scores[enemySlot] ?? null,
    mode: state.config.mode,
    mySlot,
    online,
    colours: {
      hull: p.hull,
      hullLow: p.hullLow,
      enemyHull: p.enemy ?? colors.danger,
      shield: p.shield,
      energy: p.energy,
      heat: p.heat,
      ready: p.ready,
    },
    stats: [statsOf(state.stats[0]), statsOf(state.stats[1])],
  };
}

/** Translates match events into the one-shot HUD events for the player's slot. */
export function hudEventsOf(
  events: readonly MatchEvent[],
  mySlot: Slot,
  state: MatchState,
): HudEvent[] {
  const out: HudEvent[] = [];
  const name = (slot: Slot | null): string | null =>
    slot === null ? null : getMech(state.mechs[slot].mechId).name;
  for (const e of events) {
    switch (e.t) {
      case 'hit':
        if (e.source === mySlot && e.target !== mySlot) {
          out.push({
            t: 'hit',
            amount: e.hp + e.shield,
            location: e.location,
            crit: e.crit,
            rear: e.rear,
            kind: e.kind,
          });
        } else if (e.target === mySlot) {
          out.push({ t: 'damaged', amount: e.hp + e.shield, location: e.location });
        }
        break;
      case 'kill':
        out.push({ t: 'kill', killer: name(e.killer), victim: name(e.victim) ?? '' });
        break;
      case 'roundEnd':
        out.push({ t: 'roundEnd', round: e.round, won: e.winner === mySlot, reason: e.reason });
        break;
      case 'roundStart':
        out.push({ t: 'roundStart', round: e.round });
        break;
      case 'live':
        out.push({ t: 'live', round: e.round });
        break;
      case 'suddenDeath':
        out.push({ t: 'suddenDeath' });
        break;
      case 'matchEnd':
        out.push({ t: 'matchEnd', won: e.winner === mySlot });
        break;
      case 'overheat':
        if (e.slot === mySlot) out.push({ t: 'overheat' });
        break;
      case 'ultCharged':
        if (e.slot === mySlot) out.push({ t: 'ultCharged' });
        break;
      case 'ability':
        if (e.slot === mySlot)
          out.push({ t: 'abilityUsed', name: getAbility(e.id).name, ultimate: e.index === 1 });
        break;
      case 'blocked':
        if (e.source === mySlot) out.push({ t: 'blocked' });
        break;
      default:
        break;
    }
  }
  return out;
}
